import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocalStorageStore } from '../stores/localStorageStore';
import themeColors from '../themeColors';
import { MixpanelEvent, sendPostHogEvent } from '../utils/mixpanelUtil';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const getSystemTheme = () =>
    window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';

  const initialStoreTheme = useLocalStorageStore.getState().theme;
  const setThemeInStore = useLocalStorageStore.getState().setTheme;
  const clearThemeInStore = useLocalStorageStore.getState().clearTheme;

  const [activeTheme, setActiveTheme] = useState(() => {
    return initialStoreTheme || getSystemTheme();
  });

  const [isSystemTheme, setIsSystemTheme] = useState(() => {
    return useLocalStorageStore.getState().theme === 'light';
  });

  const themeChangedByUser = useRef(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const newSystemTheme = e.matches ? 'dark' : 'light';
      if (isSystemTheme) {
        setActiveTheme(newSystemTheme);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isSystemTheme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', activeTheme);

    if (isSystemTheme) {
      clearThemeInStore();
    } else {
      setThemeInStore(activeTheme);
    }

    if (themeChangedByUser.current) {
      MixpanelEvent('settings_dark_mode_toggled', { mode: activeTheme });
      sendPostHogEvent('settings_dark_mode_toggled', { mode: activeTheme });
      themeChangedByUser.current = false;
    }
  }, [activeTheme, isSystemTheme, setThemeInStore, clearThemeInStore]);

  const toggleTheme = () => {
    themeChangedByUser.current = true;
    setIsSystemTheme(false);
    setActiveTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const resetToSystemTheme = () => {
    themeChangedByUser.current = true;
    setIsSystemTheme(true);
    setActiveTheme(getSystemTheme());
  };

  const colors = themeColors[activeTheme];

  const contextValue = useMemo(
    () => ({
      colorScheme: activeTheme,
      toggleTheme,
      resetToSystemTheme,
      colors,
      isSystemTheme,
    }),
    [activeTheme, colors, isSystemTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
